import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import {
  AppState,
  AppTheme,
  ProfileReducerState,
  DiscipleshipClassesReducerState,
  DiscipleshipClassInterface,
  DiscipleshipClassReducerState
} from "../../../interfaces";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { DialogModal, ErrorView } from "../../../components";
import { getDiscipleshipClasses, deleteDiscipleshipClass, clearDiscipleshipClassError } from "../../../store/actions";
import { checkUserAccess } from "../../../util/permission-checker";
import PopupMenuDialog from "../../../components/PopupMenuDialog";
import dateformat from "dateformat";
import UpsertDiscipleshipClassDetailsModal from "./Modals/UpsertDiscipleshipClassDetailsDialog";
import UserDiscipleshipClassDialog from "./Modals/UserDiscipleshipClassDialog";

interface DiscipleshipClassesProps {
  discipleshipClasses: DiscipleshipClassesReducerState;
  discipleshipClass: DiscipleshipClassReducerState;
  profile: ProfileReducerState;
  appTheme: AppTheme;
  getDiscipleshipClasses: (search: string, page: number, per_page: number) => void;
  clearDiscipleshipClassError: () => void;
  deleteDiscipleshipClass: (_id: string) => void;
}

const useStyles = (appTheme: AppTheme) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        width: "100%"
      },
      paper: {
        backgroundColor: appTheme.section.backgroundColor,
        width: "100%",
        padding: 30,
        marginTop: 20,
        marginBottom: 20
      },
      icon: {
        color: theme.palette.text.primary,
        padding: 10,
        borderRadius: 30,
        backgroundColor: appTheme.iconBackgroundColor,
        "&:hover": {
          backgroundColor: appTheme.iconBackgroundHoverColor
        }
      },
      searchButton: {
        marginRight: "-21px",
        height: "55px",
        borderRadius: "30px",
        width: "100px"
      },
      textInput: {
        marginTop: "5px",
        width: "300px"
      },
      textInputControl: {
        borderRadius: "30px",
        backgroundColor: appTheme.section.backgroundColor
      },
      container: {
        minHeight: "60vh"
      },
      textInputIcon: {
        color: "#000"
      }
    })
  );

const DiscipleshipClasses: React.FC<DiscipleshipClassesProps> = (props) => {
  const { appTheme, discipleshipClasses, discipleshipClass, profile, getDiscipleshipClasses, deleteDiscipleshipClass, clearDiscipleshipClassError } =
    props;

  const [search, setSearch] = useState<string>(discipleshipClasses.search);
  const [page, setPage] = useState<number>(discipleshipClasses.page);
  const [perPage, setPerPage] = useState<number>(discipleshipClasses.per_page ?? 20);
  const [selectedRecord, setSelectedRecord] = useState<DiscipleshipClassInterface | null>(null);
  const [popUpDialogOpen, setPopUpDialogOpen] = useState<boolean>(false);

  const [upsertDiscipleshipClassDetailsModelOpen, setUpsertDiscipleshipClassDetailsModelOpen] = useState<boolean>(false);
  const [userDiscipleshipClassModelOpen, setUserDiscipleshipClassModelOpen] = useState<boolean>(false);

  const popUpMenuItems = useMemo(() => {
    return [
      { text: "View Discipleship Class Students", permission: "view-discipleship-class-students" },
      { text: "Update Discipleship Class Details", permission: "edit-discipleship-class" },
      { text: "Delete Discipleship Class", permission: "delete-discipleship-class" }
    ];
  }, []);

  const [menuItems, setMenuItems] = useState<Array<string>>(
    popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text)
  );

  useEffect(() => {
    setMenuItems(popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text));
  }, [popUpMenuItems, profile.permissions]);

  useEffect(() => {
    getDiscipleshipClasses(search, page, perPage);
  }, [search, page, perPage, getDiscipleshipClasses]);

  const styles = useStyles(appTheme)();

  type DialogOptions = {
    open: boolean;
    body: string;
  };

  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({ open: false, body: "" });

  const dismissDialogModal = useCallback(() => {
    setDialogOptions({ open: false, body: "" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      clearDiscipleshipClassError();
    };
  }, [clearDiscipleshipClassError]);

  useEffect(() => {
    if (userDiscipleshipClassModelOpen) {
      return;
    }

    if (!discipleshipClass.error && discipleshipClass.response) {
      if (discipleshipClass.response.data.status === "Ok") {
        clearDiscipleshipClassError();
        return;
      }
      clearDiscipleshipClassError();

      setSelectedRecord(null);

      setUpsertDiscipleshipClassDetailsModelOpen(false);

      setUserDiscipleshipClassModelOpen(false);

      setDialogOptions({ open: true, body: discipleshipClass.response.data.status });

      getDiscipleshipClasses("", 0, perPage);
    }
  }, [
    userDiscipleshipClassModelOpen,
    discipleshipClass.error,
    discipleshipClass.response,
    perPage,
    getDiscipleshipClasses,
    clearDiscipleshipClassError
  ]);

  useEffect(() => {
    if (upsertDiscipleshipClassDetailsModelOpen || userDiscipleshipClassModelOpen) {
      return;
    }

    const requestData = discipleshipClass.error ? discipleshipClass : null;

    if (!requestData) {
      return;
    }

    if (requestData.error && requestData.error.data && requestData.error.status) {
      let body: string = requestData.error.data.status;

      if (requestData.error.status === 500) {
        body = requestData.error.data.content;
      }

      setDialogOptions({ open: true, body: body });
    } else {
      if (requestData.error && requestData.error.message) {
        setDialogOptions({ open: true, body: requestData.error.message });
      }
    }
  }, [discipleshipClass, userDiscipleshipClassModelOpen, upsertDiscipleshipClassDetailsModelOpen]);

  const onMenuItemSelected = (menuItem: string) => {
    setPopUpDialogOpen(false);

    if (!selectedRecord) {
      return;
    }

    switch (menuItem) {
      case "View Discipleship Class Students":
        setUserDiscipleshipClassModelOpen(true);
        break;
      case "Update Discipleship Class Details":
        setUpsertDiscipleshipClassDetailsModelOpen(true);
        break;
      case "Delete Discipleship Class":
        if (window.confirm("Would you like to delete this discipleship class ?")) {
          deleteDiscipleshipClass(selectedRecord.discipleship_class_id);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.root}>
      <DialogModal open={dialogOptions.open} body={dialogOptions.body} onDismissed={dismissDialogModal} />
      <UpsertDiscipleshipClassDetailsModal
        open={upsertDiscipleshipClassDetailsModelOpen}
        onDismissed={() => setUpsertDiscipleshipClassDetailsModelOpen(false)}
        discipleshipClassData={selectedRecord}
      />
      <UserDiscipleshipClassDialog
        open={userDiscipleshipClassModelOpen}
        onDismissed={() => {
          setUserDiscipleshipClassModelOpen(false);
          getDiscipleshipClasses(search, page, perPage);
        }}
        discipleshipClassData={selectedRecord}
      />
      <PopupMenuDialog
        open={popUpDialogOpen}
        onDismissed={() => setPopUpDialogOpen(false)}
        onMenuItemSelected={onMenuItemSelected}
        menuItems={menuItems}
      />
      <Container maxWidth="lg">
        {discipleshipClasses.error ? (
          <ErrorView error={discipleshipClasses.error} onRetry={() => getDiscipleshipClasses(search, page, perPage)} />
        ) : (
          <React.Fragment>
            <Paper className={styles.paper}>
              <Grid container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      {(discipleshipClasses.loading || discipleshipClass.loading) && (
                        <CircularProgress style={{ display: "block", margin: "auto" }} color="primary" />
                      )}
                    </Grid>
                    <Grid item>
                      <Typography variant="h3" color="primary">
                        Discipleship Classes
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(discipleshipClass) => {
                      setSearch(discipleshipClass.currentTarget.value);
                    }}
                    error={false}
                    value={search}
                    variant="outlined"
                    placeholder="Search ..."
                    className={styles.textInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className={styles.textInputIcon} />
                        </InputAdornment>
                      ),
                      classes: { root: styles.textInputControl }
                    }}
                  />
                </Grid>
              </Grid>
              <br />
              <TableContainer className={styles.container}>
                <Table stickyHeader aria-label="discipleship-classes">
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "ID", minWidth: 100 },
                        { label: "Title", minWidth: 250 },
                        { label: "Students", minWidth: 100 },
                        { label: "Status", minWidth: 100 },
                        { label: "Started On", minWidth: 100 },
                        { label: "Completed On", minWidth: 100 },
                        { label: "Options", minWidth: 100 }
                      ].map((column) => (
                        <TableCell key={column.label} align="center" style={{ minWidth: column.minWidth }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discipleshipClasses.data.map((discipleshipClass: DiscipleshipClassInterface) => {
                      const _id = discipleshipClass.discipleship_class_id;
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={_id}>
                          {["discipleship_class_id", "title", "students", "status", "started_on", "completed_on", "options"].map((column) => {
                            const key = `${_id}-${column}`;

                            const data = (discipleshipClass as unknown as { [key: string]: any })[column];
                            if (column === "options") {
                              if (menuItems.length === 0) return <TableCell key={key} align="center"></TableCell>;
                              return (
                                <TableCell key={key} align="center">
                                  <Button
                                    variant="outlined"
                                    disabled={menuItems.length === 0}
                                    onClick={() => {
                                      setSelectedRecord(discipleshipClass);
                                      setPopUpDialogOpen(true);
                                    }}>
                                    Options
                                  </Button>
                                </TableCell>
                              );
                            } else if (column === "started_on" || column === "completed_on") {
                              return (
                                <TableCell key={key} align="center">
                                  {data ? dateformat(data, "ddd mmm dd yyyy") : ""}
                                </TableCell>
                              );
                            } else if (column === "status") {
                              return (
                                <TableCell key={key} align="center">
                                  {data === "registration"
                                    ? "Registration Ongoing"
                                    : data === "ongoing"
                                    ? data === "Class Ongoing"
                                    : "Class Completed"}
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell key={key} align="center">
                                  {data}
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justifyContent="space-between" style={{ margin: "0px 10px 0px 10px" }}>
                <Grid item>
                  <Button
                    disabled={!checkUserAccess("add-discipleship-class", profile.permissions)}
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setSelectedRecord(null);
                      setUpsertDiscipleshipClassDetailsModelOpen(true);
                    }}>
                    Add New Discipleship Class
                  </Button>
                </Grid>
                <Grid item>
                  <TablePagination
                    component="div"
                    count={discipleshipClasses.total}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[20, 50, 100, 1000]}
                    onRowsPerPageChange={(discipleshipClass) => {
                      setPerPage(parseInt(discipleshipClass.target.value));
                      setPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </React.Fragment>
        )}
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, void, AnyAction>) => {
  return {
    getDiscipleshipClasses: (search: string, page: number, per_page: number) => dispatch(getDiscipleshipClasses(search, page, per_page)),
    clearDiscipleshipClassError: () => dispatch(clearDiscipleshipClassError()),
    deleteDiscipleshipClass: (_id: string) => dispatch(deleteDiscipleshipClass(_id))
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    discipleshipClasses: state.discipleshipClasses,
    discipleshipClass: state.discipleshipClass,
    profile: state.profile,
    appTheme: state.general.theme
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DiscipleshipClasses);
